import { useState, useEffect, useMemo, useCallback } from 'react';
import request from 'superagent';
import config from '../config/kystverket';
import { olUtils } from '@avinet/react-openlayers';

export default function useSearch({ query, coordinates, searchRadius, searchTitleFormats }) {
  const [items, setItems] = useState();
  const [loading, setLoading] = useState(false);
  const [bufferedQuery, setBufferedQuery] = useState();
  const [datasetResults, setDatasetResults] = useState({});

  useEffect(() => {
    let tId;
    if (query && query.length < 3) {
      return;
    }
    tId = setTimeout(() => {
      setBufferedQuery(query);
    }, 500);

    return () => clearTimeout(tId);
  }, [query]);

  const datasetAliases = useMemo(() => {
    return Object.values(config.themes)
      .map(element => {
        if (element.inSearch === false) return null;
        return element.dataset_alias;
      })
      .filter(Boolean);
  }, []);

  const getItemType = useCallback(item => {
    if (!item) return;
    return item.split('/')[2];
  }, []);

  useMemo(() => {
    const fetchData = async () => {
      try {
        const responses = await Promise.all(
          datasetAliases.map(alias =>
            request
              .get(`${config.adaptive4Url}api/core/v1/ogc/features/collections/${alias}`)
              .then(res => ({ alias, data: res.body }))
          )
        );

        const results = responses.reduce((acc, { alias, data }) => {
          acc[alias] = data;
          return acc;
        }, {});

        setDatasetResults(results);
      } catch (err) {
        console.error('Error fetching dataset aliases:', err);
      }
    };

    fetchData();
  }, [datasetAliases]);

  const searchPayload = useMemo(() => {
    return {
      query: bufferedQuery,
      lang: 'en',
      limit: 1000,
      datasets: datasetAliases
    };
  }, [bufferedQuery, datasetAliases]);

  useEffect(() => {
    if (!searchPayload.query) return;
    setLoading(true);
    setItems(null);

    const req = request.post(`${config.adaptive4Url}api/search/v1/data`, searchPayload);

    req
      .then(res => res.body.data)
      .then(res => {
        if (!res) {
          setLoading(false);
          setItems(null);
          return;
        }

        // Merge dataset title and alias into each result
        const enrichedResults = res.map(result => {
          // First: match datasetResults
          const matchingEntry = Object.entries(datasetResults).find(
            ([_, ds]) => ds.id === result.resource
          );

          if (matchingEntry) {
            const [alias, ds] = matchingEntry;
            const matchingTheme = Object.values(config.themes).find(
              theme => theme.dataset_alias === alias
            );
            return {
              ...result,
              datasetTitle: ds.title,
              datasetAlias: alias,
              idToUse: matchingTheme ? matchingTheme.id : null,
              path: matchingTheme ? matchingTheme.url : null,
              type: getItemType(matchingTheme.url),
              olFeature: olUtils.createFeatureFromWkt(result.data.geom || result.data.route_line, {
                type: getItemType(matchingTheme.url)
              }),
              searchTitleFormat: searchTitleFormats?.[alias] || '',
              searchResultOrder: matchingTheme ? matchingTheme.searchResultOrder : null
            };
          }
          return result;
        });

        setItems(enrichedResults);
        setLoading(false);
      })
      .catch(err => {
        if (err.code && err.code === 'ABORTED') return;

        setItems(null);
        setLoading(false);
        console.warn('Request failed', err);
      });

    return () => req.abort();
  }, [datasetResults, getItemType, searchPayload, searchTitleFormats]);

  const clickInMapPayload = useMemo(() => {
    if (!coordinates || !searchRadius) return null;

    const sources = datasetAliases.map(alias => ({ dataset: alias }));

    return {
      coordinate: coordinates,
      coordinateCrs: config.srid.toString(),
      crs: config.srid.toString(),
      distance: parseInt(searchRadius),
      limit: 1000,
      sources: sources
    };
  }, [coordinates, datasetAliases, searchRadius]);

  useEffect(() => {
    if (!coordinates || !searchRadius) return;

    const fetchData = async () => {
      try {
        const response = await request.post(
          `${config.adaptive4Url}api/core/v1/ogc/features/collections/nearest.json`,
          clickInMapPayload
        );

        // Merge dataset title and alias into each result
        const enrichedResults = response.body.data.map(result => {
          // First: match datasetResults
          const matchingEntry = Object.entries(datasetResults).find(
            ([_, ds]) => ds.id === result['@dataset']
          );

          if (matchingEntry) {
            const [alias, ds] = matchingEntry;

            // Second: find matching theme by dataset_alias
            const matchingTheme = Object.values(config.themes).find(
              theme => theme.dataset_alias === alias
            );

            return {
              ...result,
              datasetTitle: ds.title,
              datasetAlias: alias,
              idToUse: matchingTheme ? matchingTheme.id : null,
              path: matchingTheme ? matchingTheme.url : null,
              type: getItemType(matchingTheme.url),
              olFeature: olUtils.createFeatureFromWkt(result.geom || result.route_line, {
                type: getItemType(matchingTheme.url)
              })
            };
          }
          return result;
        });

        setItems(enrichedResults);
      } catch (error) {
        console.error('Error fetching search data:', error);
        setItems(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clickInMapPayload, coordinates, datasetResults, getItemType, searchRadius]);

  return { items, loading };
}
